import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  History,
  Search,
  Filter,
  User,
  FileText,
  AlertTriangle,
  ClipboardList,
  Settings,
  Loader2,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Plus,
  Edit2,
  Trash2,
  ArrowRightLeft
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

import { API_BASE } from '../utils/apiClient';

const API = API_BASE;

const AuditLog = () => {
  const { getAuthHeaders, isAdmin, isDemoReadonly } = useAuth();
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  
  // Filters
  const [entityTypeFilter, setEntityTypeFilter] = useState('all');
  const [actionFilter, setActionFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState(isDemoReadonly() ? 'all' : '7d');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);

  useEffect(() => {
    if (isAdmin()) {
      fetchAuditLogs();
    }
  }, []);

  useEffect(() => {
    applyFilters();
  }, [logs, entityTypeFilter, actionFilter, searchQuery, dateRange]);

  const fetchAuditLogs = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/audit-logs`, {
        params: { limit: 500 },
        headers: getAuthHeaders()
      });
      setLogs(response.data.logs || response.data || []);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      toast.error(t('auditLog.fetchError'));
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...logs];
    
    // Filter by entity type
    if (entityTypeFilter !== 'all') {
      filtered = filtered.filter(log => log.entity_type === entityTypeFilter);
    }
    
    // Filter by action
    if (actionFilter !== 'all') {
      filtered = filtered.filter(log => log.action === actionFilter);
    }
    
    // Filter by date range
    const now = new Date();
    const cutoffDate = new Date();
    switch (dateRange) {
      case '24h':
        cutoffDate.setHours(now.getHours() - 24);
        break;
      case '7d':
        cutoffDate.setDate(now.getDate() - 7);
        break;
      case '30d':
        cutoffDate.setDate(now.getDate() - 30);
        break;
      case '90d':
        cutoffDate.setDate(now.getDate() - 90);
        break;
      default:
        // All time - no filter
        break;
    }
    
    if (dateRange !== 'all') {
      filtered = filtered.filter(log => {
        const logDate = new Date(log.created_at);
        return logDate >= cutoffDate;
      });
    }
    
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(log => 
        log.user_name?.toLowerCase().includes(query) ||
        log.entity_id?.toLowerCase().includes(query) ||
        log.details?.toLowerCase().includes(query) ||
        log.field_changed?.toLowerCase().includes(query)
      );
    }
    
    setFilteredLogs(filtered);
    setCurrentPage(1);
  };

  const getEntityIcon = (entityType) => {
    switch (entityType) {
      case 'action':
        return <FileText className="w-4 h-4 text-blue-600" />;
      case 'user':
        return <User className="w-4 h-4 text-green-600" />;
      case 'incident':
        return <AlertTriangle className="w-4 h-4 text-red-600" />;
      case 'checklist':
        return <ClipboardList className="w-4 h-4 text-purple-600" />;
      case 'tour':
        return <History className="w-4 h-4 text-amber-600" />;
      case 'zone':
        return <Settings className="w-4 h-4 text-slate-600" />;
      default:
        return <FileText className="w-4 h-4 text-slate-400" />;
    }
  };

  const getActionBadge = (action) => {
    switch (action) {
      case 'create':
        return <Badge className="bg-green-100 text-green-700"><Plus className="w-3 h-3 mr-1" />{t('auditLog.actions.create')}</Badge>;
      case 'update':
        return <Badge className="bg-blue-100 text-blue-700"><Edit2 className="w-3 h-3 mr-1" />{t('auditLog.actions.update')}</Badge>;
      case 'delete':
        return <Badge className="bg-red-100 text-red-700"><Trash2 className="w-3 h-3 mr-1" />{t('auditLog.actions.delete')}</Badge>;
      case 'status_change':
        return <Badge className="bg-amber-100 text-amber-700"><ArrowRightLeft className="w-3 h-3 mr-1" />{t('auditLog.actions.statusChange')}</Badge>;
      case 'archive':
        return <Badge className="bg-slate-100 text-slate-700">{t('auditLog.actions.archive')}</Badge>;
      case 'restore':
        return <Badge className="bg-purple-100 text-purple-700">{t('auditLog.actions.restore')}</Badge>;
      default:
        return <Badge variant="outline">{action}</Badge>;
    }
  };

  const formatDate = (dateStr) => {
    try {
      return format(parseISO(dateStr), 'dd MMM yyyy HH:mm', { locale: i18n.language === 'fr' ? fr : undefined });
    } catch {
      return dateStr;
    }
  };

  // Pagination
  const totalPages = Math.ceil(filteredLogs.length / itemsPerPage);
  const paginatedLogs = filteredLogs.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  if (!isAdmin()) {
    return (
      <div className="p-6">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-6 text-center">
            <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-red-700">{t('auditLog.accessDenied')}</h2>
            <p className="text-red-600 mt-2">{t('auditLog.adminOnly')}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="audit-log-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2" data-testid="page-title">
            <History className="w-7 h-7 text-blue-600" />
            {t('auditLog.title')}
          </h1>
          <p className="text-slate-600 mt-1">
            {t('auditLog.subtitle')}
          </p>
        </div>
        <Button variant="outline" onClick={fetchAuditLogs} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          {t('common.refresh')}
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="text-sm font-medium text-slate-700 mb-1 block">{t('auditLog.search')}</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder={t('auditLog.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            <div className="w-[160px]">
              <label className="text-sm font-medium text-slate-700 mb-1 block">{t('auditLog.entityType')}</label>
              <Select value={entityTypeFilter} onValueChange={setEntityTypeFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('auditLog.allTypes')}</SelectItem>
                  <SelectItem value="action">{t('auditLog.types.action')}</SelectItem>
                  <SelectItem value="user">{t('auditLog.types.user')}</SelectItem>
                  <SelectItem value="incident">{t('auditLog.types.incident')}</SelectItem>
                  <SelectItem value="checklist">{t('auditLog.types.checklist')}</SelectItem>
                  <SelectItem value="tour">{t('auditLog.types.tour')}</SelectItem>
                  <SelectItem value="zone">{t('auditLog.types.zone')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="w-[160px]">
              <label className="text-sm font-medium text-slate-700 mb-1 block">{t('auditLog.action')}</label>
              <Select value={actionFilter} onValueChange={setActionFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('auditLog.allActions')}</SelectItem>
                  <SelectItem value="create">{t('auditLog.actions.create')}</SelectItem>
                  <SelectItem value="update">{t('auditLog.actions.update')}</SelectItem>
                  <SelectItem value="delete">{t('auditLog.actions.delete')}</SelectItem>
                  <SelectItem value="status_change">{t('auditLog.actions.statusChange')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="w-[160px]">
              <label className="text-sm font-medium text-slate-700 mb-1 block">{t('auditLog.period')}</label>
              <Select value={dateRange} onValueChange={setDateRange}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="24h">{t('auditLog.periods.24h')}</SelectItem>
                  <SelectItem value="7d">{t('auditLog.periods.7d')}</SelectItem>
                  <SelectItem value="30d">{t('auditLog.periods.30d')}</SelectItem>
                  <SelectItem value="90d">{t('auditLog.periods.90d')}</SelectItem>
                  <SelectItem value="all">{t('auditLog.periods.all')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-green-700">
              {filteredLogs.filter(l => l.action === 'create').length}
            </p>
            <p className="text-sm text-green-600">{t('auditLog.actions.create')}</p>
          </CardContent>
        </Card>
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-blue-700">
              {filteredLogs.filter(l => l.action === 'update').length}
            </p>
            <p className="text-sm text-blue-600">{t('auditLog.actions.update')}</p>
          </CardContent>
        </Card>
        <Card className="bg-red-50 border-red-200">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-red-700">
              {filteredLogs.filter(l => l.action === 'delete').length}
            </p>
            <p className="text-sm text-red-600">{t('auditLog.actions.delete')}</p>
          </CardContent>
        </Card>
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-amber-700">
              {filteredLogs.filter(l => l.action === 'status_change').length}
            </p>
            <p className="text-sm text-amber-600">{t('auditLog.actions.statusChange')}</p>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="w-5 h-5" />
            {t('auditLog.results')} ({filteredLogs.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : filteredLogs.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <History className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>{t('auditLog.noResults')}</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[180px]">{t('auditLog.columns.date')}</TableHead>
                      <TableHead>{t('auditLog.columns.user')}</TableHead>
                      <TableHead>{t('auditLog.columns.entityType')}</TableHead>
                      <TableHead>{t('auditLog.columns.action')}</TableHead>
                      <TableHead>{t('auditLog.columns.details')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedLogs.map((log) => (
                      <TableRow key={log.id} data-testid={`audit-row-${log.id}`}>
                        <TableCell className="text-sm text-slate-600">
                          {formatDate(log.created_at)}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-slate-400" />
                            <span className="font-medium">{log.user_name || 'N/A'}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {getEntityIcon(log.entity_type)}
                            <span className="capitalize">{t(`auditLog.types.${log.entity_type}`) || log.entity_type}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {getActionBadge(log.action)}
                        </TableCell>
                        <TableCell className="max-w-[300px]">
                          <div className="text-sm">
                            {log.field_changed && (
                              <div className="text-slate-500">
                                <span className="font-medium">{log.field_changed}:</span>{' '}
                                <span className="text-red-500 line-through">{log.old_value?.substring(0, 30)}</span>
                                {' → '}
                                <span className="text-green-600">{log.new_value?.substring(0, 30)}</span>
                              </div>
                            )}
                            {log.details && (
                              <div className="text-slate-600 truncate" title={log.details}>
                                {log.details}
                              </div>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <p className="text-sm text-slate-500">
                    {t('auditLog.pagination', { 
                      start: (currentPage - 1) * itemsPerPage + 1, 
                      end: Math.min(currentPage * itemsPerPage, filteredLogs.length),
                      total: filteredLogs.length 
                    })}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="flex items-center px-3 text-sm">
                      {currentPage} / {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AuditLog;
